// Group management utility
class GroupManager {
  constructor() {
    this.defaultGroups = [
      'משפחה',
      'חברים', 
      'עבודה',
      'שכנים',
      'אחר'
    ]
    
    this.defaultColors = [
      '#FFE4E1', // Misty Rose
      '#F0F8FF', // Alice Blue
      '#F5FFFA', // Mint Cream
      '#FFF8DC', // Cornsilk
      '#E6E6FA', // Lavender
      '#FDF5E6', // Old Lace
      '#F0FFF0', // Honeydew
      '#FFE4B5', // Moccasin
      '#E0FFFF', // Light Cyan
      '#FFEFD5', // Papaya Whip
      '#D3D3D3', // Light Gray
      '#FAFAD2'  // Light Goldenrod Yellow
    ]
    
    this.storageKey = 'wedding-app-groups'
    this.initializeGroups()
  }
  
  // Initialize groups from localStorage or defaults
  initializeGroups() {
    const stored = localStorage.getItem(this.storageKey)
    if (stored) {
      try {
        this.groupsData = JSON.parse(stored)
        // Ensure all default groups are present
        this.ensureDefaultGroups()
      } catch (e) {
        console.error('Error parsing stored groups:', e)
        this.createDefaultGroups()
      }
    } else {
      this.createDefaultGroups()
    }
  }
  
  // Create the initial default groups with colors
  createDefaultGroups() {
    this.groupsData = {}
    this.defaultGroups.forEach((group, index) => {
      this.groupsData[group] = {
        name: group,
        color: this.defaultColors[index % this.defaultColors.length],
        isDefault: true,
        created: new Date().toISOString()
      }
    })
    this.saveToStorage()
  }
  
  // Ensure all default groups exist (in case some were deleted)
  ensureDefaultGroups() {
    let changed = false
    this.defaultGroups.forEach((group, index) => {
      if (!this.groupsData[group]) {
        this.groupsData[group] = {
          name: group,
          color: this.defaultColors[index % this.defaultColors.length],
          isDefault: true,
          created: new Date().toISOString()
        }
        changed = true
      }
    })
    if (changed) {
      this.saveToStorage()
    }
  }
  
  // Get all groups as array
  getAllGroups() {
    return Object.keys(this.groupsData)
  }
  
  // Get group color
  getGroupColor(groupName) {
    if (!groupName) return '#f3f4f6' // Default gray for empty groups
    
    const group = this.groupsData[groupName]
    if (group) {
      return group.color
    }
    
    // If group doesn't exist, generate a color and add it
    return this.addGroup(groupName)
  }
  
  // Add a new group (manually or auto-detected from guest list)
  addGroup(groupName, color = null) {
    if (!groupName || this.groupsData[groupName]) {
      return this.groupsData[groupName]?.color || '#f3f4f6'
    }
    
    // Generate color if not provided
    if (!color) {
      color = this.generateColorForGroup(groupName)
    }
    
    this.groupsData[groupName] = {
      name: groupName,
      color: color,
      isDefault: false,
      created: new Date().toISOString()
    }
    
    // Save to localStorage immediately
    this.saveToStorage()
    
    console.log(`Group "${groupName}" added and saved to localStorage with color ${color}`)
    return color
  }
  
  // Generate consistent color for a group name
  generateColorForGroup(groupName) {
    let hash = 0
    for (let i = 0; i < groupName.length; i++) {
      hash = groupName.charCodeAt(i) + ((hash << 5) - hash)
    }
    return this.defaultColors[Math.abs(hash) % this.defaultColors.length]
  }
  
  // Update groups based on guest list (detect new groups)
  updateFromGuestList(guests) {
    if (!guests || guests.length === 0) return
    
    const guestGroups = guests
      .filter(guest => guest.guest_group && guest.guest_group.trim())
      .map(guest => guest.guest_group.trim())
    
    const uniqueGroups = [...new Set(guestGroups)]
    let hasChanges = false
    
    uniqueGroups.forEach(group => {
      if (!this.groupsData[group]) {
        this.addGroup(group)
        hasChanges = true
      }
    })
    
    return hasChanges
  }
  
  // Get groups data with colors
  getGroupsWithColors() {
    return { ...this.groupsData }
  }
  
  // Remove a group (only if it's not default and not used by any guest)
  removeGroup(groupName, guests = []) {
    const group = this.groupsData[groupName]
    if (!group) return false
    
    // Don't allow removing default groups
    if (group.isDefault) return false
    
    // Don't allow removing if group is in use
    const isInUse = guests.some(guest => guest.guest_group === groupName)
    if (isInUse) return false
    
    delete this.groupsData[groupName]
    this.saveToStorage()
    return true
  }
  
  // Save to localStorage
  saveToStorage() {
    const dataToSave = JSON.stringify(this.groupsData)
    localStorage.setItem(this.storageKey, dataToSave)
    
    console.log(`Groups saved to localStorage: ${Object.keys(this.groupsData).length} groups`)
    
    // Dispatch custom event to notify components
    window.dispatchEvent(new CustomEvent('groupsUpdated', {
      detail: { groups: this.getAllGroups() }
    }))
  }
  
  // Get groups data from localStorage (for debugging)
  getStoredData() {
    const stored = localStorage.getItem(this.storageKey)
    return stored ? JSON.parse(stored) : null
  }
  
  // Clear all groups and reset to defaults
  resetToDefaults() {
    this.createDefaultGroups()
    console.log('Groups reset to defaults')
  }
  
  // Debug method to show current state
  debugGroups() {
    console.log('Current groups in memory:', this.groupsData)
    console.log('Current groups in localStorage:', this.getStoredData())
    console.log('Available groups:', this.getAllGroups())
  }
}

// Create singleton instance
const groupManager = new GroupManager()

export default groupManager
